import { mapState, mapGetters } from 'vuex';
import { localizeDate } from '@/utils/date';
import ApiService from '@/services/apiService';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import { transformSearchItemsForDisplayMixin } from '@/mixins/multiSearchUtils';
import packetChartMixin from '@/components/core/virtualDevice/packetChartMixin';
import io from 'socket.io-client';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
const oneDayInMilliseconds = 86400 * 1000;
const defaultDuration = 7 * oneDayInMilliseconds;
const statisticsFetchModeFull = 'full';
export default {
  name: 'CoreVirtualDeviceSmartGroupsDashboard',
  mixins: [transformSearchItemsForDisplayMixin, multiSearchMixin, packetChartMixin],
  props: {
    accounts: {
      type: Array,
      required: true
    },
    bridgeTypesProp: {
      type: Array,
      required: true
    },
    viewModel: {
      type: Object,
      required: false,
      default: null
    }
  },
  data: function () {
    return {
      // setInterval ID for updating statistics every X seconds
      updateInterval: null,
      tabFocusListener: null,
      // only fetch the total device count every half hour, not for each new packet via websocket
      lastTotalCountFetch: 0,
      lastGraphsDataFetch: 0,
      // track if this is the initial load to show spinners only on first load
      isInitialLoad: true,
      // track if this is a manual refresh to show spinners only for manual refresh
      isManualRefresh: false,
      tabIsInFocus: true,
      priorIntervalDataFetchTs: 0,
      cachedHourlyPriorData: [],
      cachedDailyPriorData: [],
      // to reduce unnecessary fetching for "active devices per day", only refresh if a NEW device comes in
      // keep all device-ids in a map for fastest access, if the websocket pushes a new packet check if it is a unknown device
      // if it already is in this map, then there is no need to perform a new fetch for `active devices per day`
      activeDeviceIds: {},
      reloadDeviceStatistics: true,
      timeRange: {
        // from: 7 days before
        from: new Date(Date.now() - defaultDuration) / 1000,
        to: Date.now() / 1000,
        endIsNow: true
      },
      alarmLogs: [],
      searchItems: [],
      searchOperator: 'AND',
      viewPackets: {
        today: 0,
        yesterday: 0,
        last24Hours: 0
      },
      packetHistory: [],
      debouncedPacketHistory: [],
      viewPacketSocket: null,
      chartsHeight: 200,
      hourlyMetricsData: {
        common: {
          labels: []
        },
        individual: [{
          data: []
        }, {
          data: []
        }]
      },
      dailyMetricsData: {
        common: {
          labels: []
        },
        individual: [{
          data: []
        }, {
          data: []
        }]
      },
      // disable device-metrics (for now)
      // https://digimondo.atlassian.net/browse/RD-7047
      // activeDevicesMetricsData: {
      //   common: {
      //     labels: [],
      //   },
      //   individual: [
      //     {
      //       data: [],
      //     },
      //     {
      //       data: [],
      //     },
      //   ],
      // },
      // totalDevicesMetricsData: {
      //   common: {
      //     labels: [],
      //   },
      //   individual: [
      //     {
      //       data: [],
      //     },
      //     {
      //       data: [],
      //     },
      //   ],
      // },
      isFetchingExecute: false,
      isLoadingAlarmLogs: false,
      displaySearchChips: []
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'language', 'permissions']),
    ...mapGetters({
      tenantId: 'authentication/tenantId'
    }),
    operationalStatus() {
      return {
        0: this.$i18n_t('NiotaClassic.Devices.form.unset'),
        1: this.$i18n_t('NiotaClassic.Devices.form.inTransit'),
        2: this.$i18n_t('NiotaClassic.Devices.form.readyForOperation'),
        3: this.$i18n_t('NiotaClassic.Devices.form.inOperation'),
        4: this.$i18n_t('NiotaClassic.Devices.form.temporaryInactive'),
        5: this.$i18n_t('NiotaClassic.Devices.form.decommissioned')
      };
    },
    operationalStatusItems() {
      return Object.keys(this.operationalStatus).map(status => {
        return {
          text: this.operationalStatus[status],
          value: status
        };
      });
    },
    filteredSearchTypes() {
      return this.searchTypes.filter(item => {
        return item.param === 'operationalStatus' || item.param === 'bridge.type';
      });
    },
    defaultSearchType() {
      return this.filteredSearchTypes.find(item => item.param === 'operationalStatus');
    },
    datePickerMinDate() {
      return new Date(Date.now() - 86400 * 31 * 1000).toISOString();
    },
    hourlyMetricOptions() {
      return cloneDeep(this.packetMetricsOptions);
    },
    hourlyMetricsComp() {
      return this.createPacketMetricsDataComp('hourlyMetricsData');
    },
    dailyMetricsComp() {
      return this.createPacketMetricsDataComp('dailyMetricsData');
    },
    activeDevicesMetricsComp() {
      return this.createPacketMetricsDataComp('activeDevicesMetricsData', this.$i18n_t('DigitalTwin.VirtualDevice.monitoringViews.detailView.activeDevices'));
    },
    totalDevicesMetricsComp() {
      return this.createPacketMetricsDataComp('totalDevicesMetricsData', this.$i18n_t('DigitalTwin.VirtualDevice.monitoringViews.detailView.totalDevices'));
    }
  },
  watch: {
    '$route.params.id': {
      handler(newV, oldV) {
        if (newV !== oldV) {
          this.resetCachingParams();
          // reset flags when switching to a different smart group
          this.isInitialLoad = true;
          this.isManualRefresh = false;
          // tear down websocket
          this.teardownWebSocket();
          this.setupWebSocket(this.$route.params.id);
        }
      }
    },
    bridgeTypesProp() {
      (this.searchTypes.find(item => item.param === 'bridge.type') || {}).items = this.bridgeTypesProp;
    },
    async viewModel() {
      this.resetCachingParams();
      // reset any search, make sure the `fetchViewDetails` publishes the new base virtual-device-count
      this.searchItems = [];
      // reset flags when switching to a new smart group
      this.isInitialLoad = true;
      this.isManualRefresh = false;
      this.fetchViewDetails(statisticsFetchModeFull);
      this.fetchAlarmLogs(this.viewModel.id);
      this.displaySearchChips = await this.transformSearchItemsForDisplay(get(this.viewModel, 'viewConfig.searchItems', []));
    }
  },
  mounted() {
    (this.searchTypes.find(item => item.param === 'operationalStatus') || {}).items = this.operationalStatusItems;
    this.setupWebSocket(this.$route.params.id);
    this.detectTabFocusLeave();

    // NOTE: disable auto-update (for now), reduce Influx load and only update graphs manually
    // auto-update, practically only as fallback if websockets do not come through
    // do not perform too frequently, to avoid annoyance
    // this.updateInterval = setInterval(() => {
    //   this.fetchViewDetailsDebounced();
    // }, 90000);
  },
  destroyed() {
    clearInterval(this.updateInterval);
    this.teardownWebSocket();
    if (this.tabFocusListener) {
      document.removeEventListener('visibilitychange', this.tabFocusListener);
    }
  },
  methods: {
    get,
    localizeDate,
    findAccountById(id) {
      return this.accounts.find(account => account.id === id);
    },
    detectTabFocusLeave() {
      this.tabFocusListener = document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          this.tabIsInFocus = false;
        } else {
          this.tabIsInFocus = true;
        }
      });
    },
    resetCachingParams() {
      // reset all caching
      this.reloadDeviceStatistics = true;
      this.lastTotalCountFetch = 0;
      this.priorIntervalDataFetchTs = 0;
      this.activeDeviceIds = {};
    },
    searchUpdated() {
      this.forceFetchViewDetails();
    },
    setTimeRange(data) {
      // reset fetch date, for new picked time-ranges always fetch the total device count
      this.lastTotalCountFetch = 0;
      this.timeRange = {
        from: data.from,
        to: data.to,
        endIsNow: Math.abs(data.to * 1000 - Date.now()) < 60000
      };
      // no debounce for this, keep the UI reactive to user-inputs
      this.fetchViewDetails(statisticsFetchModeFull);
      this.fetchAlarmLogs(this.viewModel.id);
    },
    // only fetch new data once every 10 seconds at most, even if a flood of live-packets come in
    fetchViewDetailsDebounced: debounce(function () {
      this.fetchViewDetails();
    }, 9000,
    // fetch immediately, and then "discard" all following requests for next 10 seconds
    {
      leading: true
    }),
    forceFetchViewDetails() {
      this.resetCachingParams();
      // set manual refresh flag to show spinners for manual refresh
      this.isManualRefresh = true;
      // no debounce for this, keep the UI reactive to user-inputs
      this.fetchViewDetails(statisticsFetchModeFull);
      // also fetch alarm logs to ensure loading state is managed
      if (this.viewModel && this.viewModel.id) {
        this.fetchAlarmLogs(this.viewModel.id);
      }
    },
    fetchViewDetails(fetchMode) {
      // only set loading states on initial load or manual refresh
      if (this.isInitialLoad || this.isManualRefresh) {
        this.isFetchingExecute = true;
        this.isLoadingPacketMetrics = true;
        this.isLoadingAlarmLogs = true;
      }
      let operationalStatus = (this.searchItems.find(item => item.param === 'operationalStatus') || {}).search;
      operationalStatus = (operationalStatus || {}).value || operationalStatus;
      let bridgeType = (this.searchItems.find(item => item.param === 'bridge.type') || {}).search;
      bridgeType = (bridgeType || {}).value || bridgeType;
      const viewId = this.$route.params.id;
      return this._fetchViewExecute(viewId, operationalStatus, bridgeType).then(data => {
        this.viewPackets = {
          ...this.viewPackets,
          devices: data.total,
          errors: data.alert,
          warnings: data.warn
        };
        this.$emit('related-templates-count', data.relatedTemplatesCount);
        if (operationalStatus === undefined && bridgeType === undefined) {
          this.$emit('virtual-device-count', data.total);
        }
        // get "general statistics" for statistics-card
        return ApiService.virtualDeviceViewGetPacketStatistics(this, viewId, operationalStatus, bridgeType);
      }).then(data => {
        this.viewPackets = {
          ...this.viewPackets,
          last24Hours: data.last24Hours.count,
          prior24Hours: data.prior24Hours.count
        };
        this.isFetchingExecute = false;

        // skip updating the graphs with historical data, unless triggered manually
        if (fetchMode === statisticsFetchModeFull) {
          return this._fetchPacketCountHistory(this.$route.params.id, operationalStatus, bridgeType);
        }
        return null;
      }).catch(e => {
        console.error('Could not fetch view', e);
      }).finally(() => {
        this.isFetchingExecute = false;
        // reset loading states on error
        this.isLoadingPacketMetrics = false;
        this.isLoadingAlarmLogs = false;
        // reset manual refresh flag after data is fetched
        this.isManualRefresh = false;
        // after initial rendering of graphs, disable animation
        setTimeout(() => {
          this.enableChartAnimations = false;
        }, 1000);
      });
    },
    // only call from within fetchViewDetails
    _fetchViewExecute(viewId, operationalStatus, bridgeType) {
      return ApiService.virtualDeviceViewsExecute(this, viewId, operationalStatus, bridgeType).then(response => {
        return response;
      }).catch(error => {
        console.error(error);
      });
    },
    // only call from within fetchViewDetails
    _fetchPacketCountHistory(viewId, operationalStatus, bridgeType) {
      if (!this.tabIsInFocus) {
        return;
      }
      // set loading state on initial load or manual refresh
      if (this.isInitialLoad || this.isManualRefresh) {
        this.isLoadingPacketMetrics = true;
        // safety timeout to prevent loading state from getting stuck
        setTimeout(() => {
          if (this.isLoadingPacketMetrics) {
            this.isLoadingPacketMetrics = false;
          }
        }, 30000);
      }
      this.lastGraphsDataFetch = Date.now();

      // show the threshold-value for packets, in either the hourly or daily chart, depending on the selected interval
      // first clean em up, and then add the threshold as "static line"
      this.hourlyMetricsData.individual = this.hourlyMetricsData.individual.slice(0, 2);
      this.dailyMetricsData.individual = this.dailyMetricsData.individual.slice(0, 2);
      if (this.viewModel.meta.monitoring.isActive && this.viewModel.meta.monitoring.threshold) {
        if (parseInt(this.viewModel.meta.monitoring.interval) === 60) {
          this.hourlyMetricsData.individual = [...this.hourlyMetricsData.individual, {
            data: Array.from(Array(24).keys()).map(() => {
              return this.viewModel.meta.monitoring.threshold;
            })
          }];
        }
        if (parseInt(this.viewModel.meta.monitoring.interval) === 1440) {
          this.dailyMetricsData.individual = [...this.dailyMetricsData.individual, {
            data: Array.from(Array(7).keys()).map(() => {
              return this.viewModel.meta.monitoring.threshold;
            })
          }];
        }
      }
      let lclTimeFrom = (Date.now() - defaultDuration) / 1000;
      if (this.timeRange.from) {
        // want to have a minimum-range of 7 days for the daily packet-statistics
        lclTimeFrom = Math.min(this.timeRange.from, lclTimeFrom);
      }
      const lclTimeTo = this.timeRange.endIsNow ? Date.now() : this.timeRange.to * 1000;
      const rangeStart = new Date(lclTimeFrom * 1000);
      const rangeTo = new Date(lclTimeTo);
      const interval = rangeTo - rangeStart;
      const priorRangeStart = new Date(rangeStart - interval);

      // show skeleton-loaders for each of these, and make them available as soon as they are finished,
      // do not wait until `isLoadingPacketMetrics` is reset, because the last request may take some seconds longer
      this.hourlyMetricsComp.common.labels = [];
      this.dailyMetricsComp.common.labels = [];
      const shouldFetchPriorData = this.priorIntervalDataFetchTs <= Date.now() - 600 * 1000;

      // =======
      // HOURLY PACKETS
      // =======

      // fetch the last 24h of the selected range, as well as the "parallel" history exactly 1 interval prior
      // NOTE: since the prior interval is fixed in the past, there is no need to refresh it with every request
      // it MAY change, if the smartgroup-filter-criteria change, but at least during this page-load keep it cached for 10min
      return Promise.allSettled([ApiService.virtualDeviceViewGetPacketStatistics(this, viewId, operationalStatus, bridgeType, new Date(lclTimeTo - oneDayInMilliseconds), rangeTo, '1h'), shouldFetchPriorData ? ApiService.virtualDeviceViewGetPacketStatistics(this, viewId, operationalStatus, bridgeType, new Date(lclTimeTo - 2 * oneDayInMilliseconds), new Date(lclTimeTo - oneDayInMilliseconds), '1h') : Promise.resolve(this.cachedHourlyPriorData)]).then(results => {
        const dataCurrent = results[0].status === 'fulfilled' ? results[0].value : [];
        const dataPrior = results[1].status === 'fulfilled' ? results[1].value : [];
        if (shouldFetchPriorData) {
          this.cachedHourlyPriorData = dataPrior;
          this.priorIntervalDataFetchTs = Date.now();
        }
        this.preparePacketMetricsData(dataCurrent, dataPrior, 'hourlyMetricOptions', 'hourlyMetricsData', true);

        // =======
        // DAILY PACKETS
        // =======
        return Promise.allSettled([ApiService.virtualDeviceViewGetPacketStatistics(this, viewId, operationalStatus, bridgeType, rangeStart, rangeTo, '1d'), shouldFetchPriorData ? ApiService.virtualDeviceViewGetPacketStatistics(this, viewId, operationalStatus, bridgeType, priorRangeStart, rangeStart, '1d') : Promise.resolve(this.cachedDailyPriorData)]);
      }).then(results => {
        const dataCurrent = results[0].status === 'fulfilled' ? results[0].value : [];
        const dataPrior = results[1].status === 'fulfilled' ? results[1].value : [];
        if (shouldFetchPriorData) {
          this.cachedDailyPriorData = dataPrior;
        }
        this.preparePacketMetricsData(dataCurrent, dataPrior, 'packetMetricsOptions', 'dailyMetricsData', true);

        // disable device-metrics (for now)
        // https://digimondo.atlassian.net/browse/RD-7047
        // // =======
        // // ACTIVE/TOTAL DEVICE STATISTICS
        // // =======
        // const reloadActiveCounts = this.lastTotalCountFetch <= Date.now() - 1800 * 1000;
        // if (this.reloadDeviceStatistics) {
        //   this.activeDevicesMetricsComp.common.labels = [];
        //   if (reloadActiveCounts) {
        //     this.totalDevicesMetricsComp.common.labels = [];
        //   }
        // }
        // return this.reloadDeviceStatistics
        //   ? ApiService.virtualDeviceViewGetDeviceStatistics(
        //       this,
        //       viewId,
        //       operationalStatus,
        //       bridgeType,
        //       rangeStart,
        //       rangeTo,
        //       '1d',
        //       // do not refresh the total devices. that is a heavy request that has no need to run every X seconds
        //       reloadActiveCounts ? ['activeCounts'] : undefined
        //     )
        //   : Promise.resolve(null);
        return null;
      }).then(() => {
        // this.reloadDeviceStatistics = false;
        // if (data === null) {
        //   return null;
        // }
        // const activeCounts = data.activeCounts.map((cnt) => {
        //   return { time: cnt.time, count: cnt.devices };
        // });
        // this.preparePacketMetricsData(activeCounts, [], 'packetMetricsOptions', 'activeDevicesMetricsData', true);
        // // contains only 1 data-set, so remove label for second one
        // if (this.activeDevicesMetricsData.individual.length > 1) {
        //   this.activeDevicesMetricsData.individual.pop();
        // }
        // if (data.totalCounts) {
        //   this.lastTotalCountFetch = Date.now();
        //   this.preparePacketMetricsData(data.totalCounts, [], 'packetMetricsOptions', 'totalDevicesMetricsData', true);
        //   // contains only 1 data-set, so remove label for second one
        //   if (this.totalDevicesMetricsData.individual.length > 1) {
        //     this.totalDevicesMetricsData.individual.pop();
        //   }
        // }
        return null;
      }).catch(error => {
        console.error(error);
      }).finally(() => {
        this.isLoadingPacketMetrics = false;
        // mark that initial load is complete
        this.isInitialLoad = false;
        // reset manual refresh flag
        this.isManualRefresh = false;
      });
    },
    async fetchAlarmLogs(smartGroupId) {
      // only set loading state on initial load or manual refresh
      if (this.isInitialLoad || this.isManualRefresh) {
        this.isLoadingAlarmLogs = true;
        // safety timeout to prevent loading state from getting stuck
        setTimeout(() => {
          if (this.isLoadingAlarmLogs) {
            this.isLoadingAlarmLogs = false;
          }
        }, 30000);
      }
      const pageSize = 100;
      let logs = [];
      let page = 1;
      let hasMore = true;
      try {
        do {
          const pageResult = await ApiService.fetchAlarmLogs(page, pageSize, [{
            variable: 'smartGroupIds',
            search: [smartGroupId]
          }, {
            variable: 'createTimestampGt',
            search: new Date(this.timeRange.from * 1000).toISOString()
          }, {
            variable: 'createTimestampLt',
            search: new Date(this.timeRange.to * 1000).toISOString()
          }]);
          logs = logs.concat(pageResult.logs);
          page += 1;
          hasMore = pageResult.pagination.moreAvailable;
        } while (hasMore);
        this.alarmLogs = logs;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoadingAlarmLogs = false;
        // mark that initial load is complete
        this.isInitialLoad = false;
        // reset manual refresh flag
        this.isManualRefresh = false;
      }
    },
    teardownWebSocket() {
      if (this.viewPacketSocket) {
        this.viewPacketSocket.close();
      }
    },
    setupWebSocket(viewId) {
      if (viewId) {
        const viewPacketSocket = io(`${location.origin}/virtualdevice/view/${viewId}/packet`, {
          path: '/api/v1/socket.io',
          allowEIO3: true
        });
        const instance = this;
        viewPacketSocket.on('connect_error', function (error) {
          console.error('Socket connection error:', error);
        });
        viewPacketSocket.on('error', function (error) {
          console.error('Socket error:', error);
        });
        viewPacketSocket.on('update', function (data) {
          let fromUnknownDevice = false;
          // keep new packets in separate array, until they are "debounced"
          instance.debouncedPacketHistory.push(...data.virtualDevices.map(item => {
            // only perform a refresh of device-statistics, if this device was not seen before
            if (!instance.activeDeviceIds[item.externalId]) {
              fromUnknownDevice = true;
              instance.activeDeviceIds[item.externalId] = true;
            }
            return {
              ...item,
              timestamp: item.time
            };
          }));
          instance.reloadDeviceStatistics = instance.reloadDeviceStatistics || fromUnknownDevice;
          // make a throttled call, do not refresh everything for every packet
          instance.throttledPushPacket();
        });
        this.viewPacketSocket = viewPacketSocket;
      }
    },
    throttledPushPacket: throttle(function () {
      this.packetHistory = [...this.debouncedPacketHistory, ...this.packetHistory];
      this.debouncedPacketHistory = [];
      this.fetchViewDetailsDebounced();
      // 30 second throttle-window
    }, 30000)
  }
};